import React, { useState, useContext, useEffect, useRef } from "react";

import Hoc from "../../../components/HOC";
import CustomDataTable from "../../../components/CustomDataTable";
import { MdModeEdit } from "react-icons/md";
import { MdCurrencyExchange } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import CustomToolTip from "../../../components/ToolTip";
import AddEmployeeModal from "./AddEmployeeModal";
import EditEmployeeModal from "./EditEmployeeModal";
import { BsPersonFillCheck } from "react-icons/bs";
import { FaFileUpload, FaSearch } from "react-icons/fa";
import { EmployeeContext } from "../../../Context/Employee";
import BackgroundCheckModal from "./BackgroundCheckModal"; 
import { toast } from "react-toastify";
import CustomDropdown from "../../../components/CustomDropdown";
import LOCAL_KEYS, { parseLocalStorageJSON } from "../../../helpers/localstorage";

const initialEmployeeValues = {
  firstName: "",
  lastName: "",
  gender: "",
  email: "",
  phoneNumber: "",
  technology: "",
  status: "",
  company: "UNISOFT",
};

const ManageEmployee = () => {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editEmployeeData, setEditEmployeeData] = useState(null);
  const [showBgCheckModal, setShowBgCheckModal] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [bgCheckData, setBgCheckData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchDebounceRef = useRef();
  
  // Pagination states - using backend defaults
  const [page, setPage] = useState(0); // Backend uses 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(10); // Default size
  const [currentPage, setCurrentPage] = useState(1); // 1-indexed for display
  
  // Filter states - these will be sent to backend
  const [statusFilter, setStatusFilter] = useState("A");
  const [companyFilter, setCompanyFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
     
  // Use the EmployeeContext to get employee data and loading state
  const { 
    employees, 
    loading: employeesLoading, 
    fetchEmployees, 
    getbgCheck,
    totalEmployees,
    totalPages
  } = useContext(EmployeeContext);
  
  const userDetails = parseLocalStorageJSON(LOCAL_KEYS.USER_DETAILS);
  const isAdmin = userDetails?.acctType === 'ADMIN';
  
  // Get filters for API request
  const getFilters = () => {
    const filters = {
      page,
      size: rowsPerPage
    };
    
    if (statusFilter) filters.stsCd = statusFilter;
    if (companyFilter) filters.company = companyFilter;
    
    // Only use searchQuery parameter for search
    if (searchQuery) {
      filters.search = searchQuery;
    }
    
    console.log('Employee filters being applied:', filters);
    return filters;
  };

  // Handler for edit button
  const handleEdit = (row) => {
    setEditEmployeeData({
      id: row.id,
      firstName: row.fname || "",
      lastName: row.lname || "",
      gender: row.gender || "",
      email: row.email || "",
      phoneNumber: row.phone || "",
      technology: row.technology || "",
      status: row.stsCd || "",
      company: row.company || "UNISOFT",
      backgroundCheck: row.backgroundCheck || ""
    });

    setShowEditModal(true);
  };

  // Handler for closing add modal
  const handleCloseAddModal = () => {
    setStatusFilter("A");
    setSearchQuery(""); // Clear the search query
    setShowAddModal(false);
    // Refresh the employee data after adding a new employee
    fetchEmployees({ ...getFilters(), stsCd: "A", search: "" }); // Pass empty search to ensure it's cleared
  };

  // Handler for background check button
  const handleBackgroundCheck = async (row) => {
    const employeeId = row.id;
    setSelectedEmployeeId(employeeId);
    setLoading(true);
    
    try {
      const data = await getbgCheck(employeeId);
      setBgCheckData(data);
      setShowBgCheckModal(true);
    } catch (error) {
      toast.error("Failed to fetch background check data");
      console.error(`Error fetching background check data for employee ${employeeId}:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Handler for closing background check modal
  const handleCloseBgCheckModal = () => {
    setShowBgCheckModal(false);
    setSelectedEmployeeId(null);
    setBgCheckData(null);
  };

  // Handler for closing edit modal
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    // Refresh the employee data with current filters (don't reset to active)
    fetchEmployees(getFilters());
  };

  const handleUpload = (row) => {
    const { fname = "", lname = "" } = row.originalData || {};
    const fullName = `${fname} ${lname}`.trim();
  
    navigate(`/manage-employees/uploaddocuments/${row.id}`, {
      state: {
        activeItem: "Employee",
        employeeName: fullName,
      },
    });
  };
  
  // Handle pagination change
  const handleChangePage = (newPage) => {
    setPage(newPage - 1); // Convert 1-indexed to 0-indexed for backend
    setCurrentPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (rowsPerPage) => {
    setRowsPerPage(rowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
    setCurrentPage(1);
  };

  // Calculate pagination info
  const getPaginationInfo = () => {
    if (totalEmployees === 0) return { start: 0, end: 0 };
    const start = page * rowsPerPage + 1;
    const end = Math.min((page + 1) * rowsPerPage, totalEmployees);
    return { start, end };
  };

  // Generate array of page numbers to display
  const getPageNumbers = () => {
    const totalPageCount = Math.ceil(totalEmployees / rowsPerPage);
    let pages = [];
    
    // Simple case: show all pages if less than 5
    if (totalPageCount <= 5) {
      for (let i = 1; i <= totalPageCount; i++) {
        pages.push(i);
      }
      return pages;
    }
    
    // More complex case: show current page and some pages around it
    if (currentPage <= 3) {
      // Near start
      return [1, 2, 3, 4, 5];
    } else if (currentPage >= totalPageCount - 2) {
      // Near end
      return [
        totalPageCount - 4,
        totalPageCount - 3,
        totalPageCount - 2,
        totalPageCount - 1,
        totalPageCount
      ];
    } else {
      // Middle
      return [
        currentPage - 2,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        currentPage + 2
      ];
    }
  };
  
  // Debounced API call for search
  useEffect(() => {
    // Check if password is expired before making API call
    const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
    if (isPasswordExpired) {
      console.log("ManageEmployee useEffect API call skipped - password expired");
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const filters = getFilters();
      fetchEmployees(filters)
        .then((data) => {
          if (data && data.content && data.content.length === 0) {
            toast.info("No consultants found");
          }
        })
        .catch((error) => {
          // Only show toast for real errors, not for 'No consultants found' or backend empty messages
          if (error?.message && !error.message.toLowerCase().includes("No consultants found for given criteria")) {
            toast.error("No consultants found for given criteria");
          }
        })
        .finally(() => {
          setIsSearching(false);
        });
    }, 500);
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line
  }, [page, rowsPerPage, statusFilter, companyFilter, searchQuery]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
    setCurrentPage(1);
  }, [statusFilter, companyFilter, searchQuery]);
  
  // Format the API data to match the table structure
  const formatEmployeeData = (rawData) => {
    return rawData.map((emp) => ({
      id: emp.id,
      name: `${emp.fname || ""} ${emp.lname || ""}`.trim(),
      technology: emp.technology || "",
      phone: emp.phone || "",
      status: emp.stsCd || "",
      backgroundCheck: emp.backgroundCheck || "",
      // Include original data for editing
      originalData: emp,
    }));
  };

  const formattedEmployees = formatEmployeeData(employees);

  // Get pagination info
  const { start, end } = getPaginationInfo();
  const pageNumbers = getPageNumbers();
  const totalPageCount = Math.ceil(totalEmployees / rowsPerPage);
  
  // On mount, always default to active
  useEffect(() => {
    setStatusFilter("A");
  }, []);
  
  // Add a callback to be called after successful add/edit
  const handleAfterSubmit = () => {
    setStatusFilter("A");
    setSearchQuery(""); // Clear the search query
    fetchEmployees({ ...getFilters(), stsCd: "A", search: "" }); // Pass empty search to ensure it's cleared
  };
  
  return (
    <div className="bg-gray-50">
      <Loader isLoading={employeesLoading || loading || isSearching} />

      <div className="p-6 max-w-7xl mx-auto">
        {isAdmin && (
          <div className="flex justify-end mb-6">
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
            >
              Add Consultant
            </button>
          </div>
        )}

        <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
          {/* Search and filters inside the table container */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search h-5 w-5 text-gray-400">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.3-4.3"></path>
                  </svg>
                  </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                  placeholder="Search consultants by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="flex space-x-2">
                <select
                  value={companyFilter}
                  onChange={e => setCompanyFilter(e.target.value)}
                  className="px-5 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Companies</option>
                  <option value="UNISOFT">UNISOFT</option>
                  <option value="DATACLOUD">DATACLOUD</option>
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-5 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="A">Active</option>
                  <option value="D">Deactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table with sticky header */}
          <div className="overflow-y-auto" style={{ height: "52vh" }}>
            <table className="min-w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                    Candidate
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Technology
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Phone
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Company
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {formattedEmployees.map((employee) => {
                  const userId = employee.id || Math.floor(Math.random() * 1000);
                  const gender = userId % 2 === 0 ? 'men' : 'women';
                  const imageNumber = userId % 70;
                  const statusText = employee.status === "A" ? "Active" : "Deactive";

                  return (
                    <tr key={employee.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <img 
                              className="h-10 w-10 rounded-full object-cover"
                              src={`https://randomuser.me/api/portraits/${gender}/${imageNumber}.jpg`}
                              alt={employee.name}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://ui-avatars.com/api/?name=${employee.name}&background=E6F0FF&color=3B82F6`;
                              }}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                            <div className="text-sm text-gray-500">{employee.originalData?.email || "N/A"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-normal text-gray-700">{employee.technology}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{employee.phone || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{employee.originalData?.company || "UNISOFT"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${employee.status === "A" ? "bg-[#34D39920] text-[#34D399]" : "bg-red-50 text-red-400"}`}>
                          {statusText}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleEdit(employee.originalData)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <MdModeEdit size={20} data-tip data-for={`editTip-${employee.id}`} />
                            <CustomToolTip id={`editTip-${employee.id}`} title="Edit" />
                          </button>
                          <button
                            onClick={() => handleBackgroundCheck(employee.originalData)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <BsPersonFillCheck size={20} data-tip data-for={`checkTip-${employee.id}`} />
                            <CustomToolTip id={`checkTip-${employee.id}`} title="Check" />
                          </button>
                          <button
                            onClick={() => handleUpload(employee)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <FaFileUpload size={20} data-tip data-for={`uploadTip-${employee.id}`} />
                            <CustomToolTip id={`uploadTip-${employee.id}`} title="Upload" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button 
                onClick={() => currentPage > 1 && handleChangePage(1)}
                disabled={currentPage === 1 || totalEmployees === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button 
                onClick={() => currentPage > 1 && handleChangePage(currentPage - 1)}
                disabled={currentPage === 1 || totalEmployees === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => currentPage < totalPageCount && handleChangePage(currentPage + 1)}
                disabled={currentPage === totalPageCount || totalEmployees === 0}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => currentPage < totalPageCount && handleChangePage(totalPageCount)}
                disabled={currentPage === totalPageCount || totalEmployees === 0}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{start}</span> to <span className="font-medium">{end}</span> of <span className="font-medium">{totalEmployees}</span> employees
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label htmlFor="rows-per-page" className="text-sm text-gray-700">
                    Rows per page:
                  </label>
                  <select
                    id="rows-per-page"
                    value={rowsPerPage}
                    onChange={(e) => handleChangeRowsPerPage(Number(e.target.value))}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => currentPage > 1 && handleChangePage(1)}
                    disabled={currentPage === 1 || totalEmployees === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    First
                  </button>
                  <button
                    onClick={() => currentPage > 1 && handleChangePage(currentPage - 1)}
                    disabled={currentPage === 1 || totalEmployees === 0}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {pageNumbers.map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => handleChangePage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 ${
                        pageNum === currentPage
                          ? 'bg-blue-50 text-blue-600'
                          : 'bg-white text-gray-700'
                      } text-sm font-medium hover:bg-gray-50`}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button
                    onClick={() => currentPage < totalPageCount && handleChangePage(currentPage + 1)}
                    disabled={currentPage === totalPageCount || totalEmployees === 0}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => currentPage < totalPageCount && handleChangePage(totalPageCount)}
                    disabled={currentPage === totalPageCount || totalEmployees === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Last
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {isAdmin && (
        <AddEmployeeModal
          show={showAddModal}
          onClose={handleCloseAddModal}
          title="Add Consultant"
          initialValues={initialEmployeeValues}
          refreshEmployees={fetchEmployees}
          handleAfterSubmit={handleAfterSubmit}
        />
      )}
      
      <BackgroundCheckModal
        show={showBgCheckModal}
        onClose={handleCloseBgCheckModal}
        employeeId={selectedEmployeeId}
        notes={bgCheckData?.backgroundcheck}
        title={bgCheckData?.message}
      />

      {/* Only render EditEmployeeModal when we have data and modal should be shown */}
      {editEmployeeData && showEditModal && (
        <EditEmployeeModal
          key={editEmployeeData.id}
          show={showEditModal}
          onClose={handleCloseEditModal}
          title="Edit Consultant"
          initialValues={editEmployeeData}
          handleAfterSubmit={handleAfterSubmit}
        />
      )}
    </div>
  );
};

export default Hoc(ManageEmployee);